/**
 * The field report's markdown, read into the few shapes the Reports panel draws.
 *
 * This is not a markdown parser. It only finds the report's STRUCTURE (the H1
 * title, the section headings, the top-level bullets and the paragraphs
 * between them) and hands every piece of actual markdown back as a string, which
 * ChatMarkdown still renders exactly as it did before. That split is what lets
 * the panel set a bullet's bold lead inline, give every paragraph its own
 * dir="auto", and turn the "Signals filed" section into links, without a second
 * markdown dependency.
 */

export type ReportBlock =
  /** One paragraph (or table, quote or code fence), still markdown. */
  | { kind: "prose"; markdown: string }
  /** A run of top-level bullets; each item is its own markdown string. */
  | { kind: "points"; items: string[] };

export interface ReportSection {
  /** null for anything written before the first heading. */
  heading: string | null;
  blocks: ReportBlock[];
}

export interface ParsedReport {
  /** The H1, with a leading "Field Report:" removed. */
  title: string | null;
  sections: ReportSection[];
}

const H1 = /^#\s+(.+?)\s*#*\s*$/;
const HEADING = /^#{1,6}\s+(.+?)\s*#*\s*$/;
const BULLET = /^ {0,3}(?:[-*+]|\d+[.)])\s+(.*)$/;
const FENCE = /^ {0,3}(```|~~~)/;
const RULE = /^ {0,3}([-*_])(\s*\1){2,}\s*$/;
/* "Field Report:" with a colon, en dash, em dash or hyphen after it. */
const FIELD_REPORT_PREFIX = /^field\s+report\s*[:\u2013\u2014-]\s*/i;

export function stripFieldReportPrefix(title: string): string {
  return title.replace(FIELD_REPORT_PREFIX, "").trim();
}

export function parseReport(content: string): ParsedReport {
  const lines = content.replace(/\r\n?/g, "\n").split("\n");
  let title: string | null = null;
  const sections: ReportSection[] = [];
  let section: ReportSection = { heading: null, blocks: [] };
  let prose: string[] = [];
  let points: string[] | null = null;
  let inFence = false;

  const flushProse = () => {
    const text = prose.join("\n").trim();
    if (text) section.blocks.push({ kind: "prose", markdown: text });
    prose = [];
  };
  const flushPoints = () => {
    if (points && points.length > 0) {
      section.blocks.push({ kind: "points", items: points.map((p) => p.trim()) });
    }
    points = null;
  };
  const flushSection = () => {
    flushProse();
    flushPoints();
    if (section.heading !== null || section.blocks.length > 0) sections.push(section);
  };

  for (const line of lines) {
    if (inFence) {
      prose.push(line);
      if (FENCE.test(line)) inFence = false;
      continue;
    }
    if (FENCE.test(line)) {
      flushPoints();
      prose.push(line);
      inFence = true;
      continue;
    }

    const h1 = H1.exec(line);
    if (h1 && title === null) {
      title = stripFieldReportPrefix(h1[1]);
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushSection();
      section = { heading: heading[1].replace(/:\s*$/, "").trim(), blocks: [] };
      continue;
    }

    if (RULE.test(line)) {
      flushProse();
      flushPoints();
      continue;
    }

    const bullet = BULLET.exec(line);
    if (bullet) {
      flushProse();
      if (!points) points = [];
      points.push(bullet[1]);
      continue;
    }

    if (line.trim() === "") {
      // A blank line ends a paragraph. A run of bullets may be loose (blank
      // lines between items), so it stays open until something else arrives.
      flushProse();
      continue;
    }

    // Indented text under a bullet is that bullet's continuation (or a nested list).
    if (points && /^\s{2,}\S/.test(line)) {
      points[points.length - 1] += `\n${line.replace(/^\s{2,4}/, "")}`;
      continue;
    }

    flushPoints();
    prose.push(line);
  }

  flushSection();
  return { title, sections };
}

/** The section that lists what the run filed onto the board. */
export function isSignalsFiledHeading(heading: string | null): boolean {
  return heading != null && /^signals?\s+filed$/i.test(heading.replace(/[*_`]/g, "").trim());
}

/** The bullets of the report's "Signals filed" section, as written. */
export function filedItems(report: ParsedReport): string[] {
  const section = report.sections.find((s) => isSignalsFiledHeading(s.heading));
  if (!section) return [];
  return section.blocks.flatMap((b) => (b.kind === "points" ? b.items : []));
}

/**
 * The title a filed bullet names: its bold lead when it has one, else the text
 * before a dash, a colon or an opening bracket, with any wrapping quotes gone.
 */
export function filedTitle(item: string): string {
  const firstLine = item.split("\n")[0].trim();
  const bold = /^\*\*(.+?)\*\*/.exec(firstLine) ?? /^__(.+?)__/.exec(firstLine);
  let title = bold
    ? bold[1]
    : (firstLine.split(/\s[\u2013\u2014-]\s|:\s|\s\(/)[0] ?? firstLine);
  title = title.replace(/[:.]\s*$/, "").replace(/[*_`]/g, "").trim();
  return title.replace(/^["'“‘]+|["'”’]+$/g, "").trim();
}

/** Lowercased letters and digits only, so punctuation and quotes never block a match. */
export function normalizeTitle(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/* -------------------------------------------------------------- briefing */

/** The report's known sections, each drawn as its own module in the briefing. */
export type SectionKind = "stories" | "desk" | "followups" | "watch" | "filed" | "other";

export function sectionKind(heading: string | null): SectionKind {
  if (!heading) return "other";
  const h = heading.replace(/[*_`]/g, "").trim().toLowerCase();
  if (isSignalsFiledHeading(heading)) return "filed";
  if (/^what changed/.test(h)) return "stories";
  if (/activity says|team'?s activity|team activity/.test(h)) return "desk";
  if (/^follow[\s-]?ups?\b/.test(h)) return "followups";
  if (/^watching\b|^watch ?list\b/.test(h)) return "watch";
  return "other";
}

/** Every point and paragraph of a section, in order, as markdown strings. */
export function sectionItems(section: ReportSection): string[] {
  return section.blocks.flatMap((b) => (b.kind === "points" ? b.items : [b.markdown]));
}

/* A bold lead at the start of a point, and whatever joins it to its sentence
   (a colon, an en or em dash, a hyphen). */
const BOLD_LEAD = /^(?:\*\*(.+?)\*\*|__(.+?)__)\s*[:\u2013\u2014-]?\s*/s;

/** A point split into its bold lead ("Pace") and the sentence it leads. */
export function splitLead(item: string): { lead: string | null; text: string } {
  const m = BOLD_LEAD.exec(item.trim());
  if (!m) return { lead: null, text: item.trim() };
  const lead = (m[1] ?? m[2] ?? "").replace(/[:.]\s*$/, "").trim();
  // The sentence after "**Lead:**" starts the reader's line, so it starts with a capital.
  const text = item
    .trim()
    .slice(m[0].length)
    .trim()
    .replace(/^\p{Ll}/u, (c) => c.toUpperCase());
  return { lead: lead || null, text };
}

/** The label a known section is printed under: sentence case, whatever case the
 *  report's own H3 used ("Signals Filed" reads "Signals filed"). Unknown sections
 *  keep their heading as written. */
export function sectionTitle(heading: string): string {
  switch (sectionKind(heading)) {
    case "stories":
      return "What changed in the market";
    case "desk":
      return "What the team\u2019s activity says";
    case "followups":
      return "Follow-ups on the last report";
    case "watch":
      return "Watching";
    case "filed":
      return "Signals filed";
    default:
      return heading;
  }
}

/** Markdown reduced to its words, for text set as a headline. */
export function plainText(md: string): string {
  return md
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/(\*\*|__|`)/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * The note a filed bullet carries after its title, past a dash or a colon
 * ("Highlighting the performance gap"), or null. Mirrors filedTitle: after the
 * bold lead, or after the first dash or colon.
 */
export function filedNote(item: string): string | null {
  const firstLine = item.split("\n")[0].trim();
  const bold = /^(?:\*\*(.+?)\*\*|__(.+?)__)/.exec(firstLine);
  let rest: string;
  if (bold) {
    rest = firstLine.slice(bold[0].length);
  } else {
    const sep = /\s[\u2013\u2014-]\s|:\s/.exec(firstLine);
    if (!sep) return null;
    rest = firstLine.slice(sep.index + sep[0].length);
  }
  const note = plainText(rest.replace(/^\s*[:\u2013\u2014-]\s*/, "")).replace(/^\((.*)\)$/, "$1");
  return note || null;
}

/** A follow-up that only says there is nothing to follow up (the first report). */
export function isInauguralNote(text: string): boolean {
  return /\b(first|inaugural|initial)\b[^.]*\breport\b|\bno (previous|prior|earlier) report/i.test(text);
}
